/**
 * Revision 매핑 — 원본 채널을 Sigma 손 슬롯과 카메라 슬롯에 연결하는 사용자 설정.
 *
 * 정본은 BE `spawning_pool_templates/mapping/type.py` 의 `Mapping`(`schema_version` 2)이다. 점검 · POC ·
 * 변환이 같은 매핑을 쓴다. 이 모듈의 매핑 타입은 정본 JSON Schema 에서 생성한 타입이다
 * (`contract/mappingSchema.generated.ts`). 그래서 필드 이름은 wire 그대로 snake_case 다.
 *
 * 구성요소(위치 · 자세 · 그리퍼 · 원점 · 손 축 · 모델 · Hz · 손끝 오프셋)는 항상 있다. 고르지 않은 값은 그 안의
 * 칸이 `null` 이다. `null` 이 될 수 있는 객체는 `action` 하나다. 빈 칸이 있는 매핑은 임시 저장만 된다. 일반
 * 저장과 POC 는 BE 가 완성본만 받는다. 빈 칸은 검증이 leaf 경로로 알린다.
 *
 * Owns:
 * - 선택지 상수, 역할별 필요 열 수, 빈 값(정본 스키마의 기본값과 같다), 중복 열 검사, 검증 결과 · POC 작업 타입.
 *
 * Does not own:
 * - 매핑 모양 — 정본 스키마와 생성 타입(`contract/`).
 * - 자세 계산 — `feature/rotationMath.ts` · `feature/mappingPose.ts`.
 */
import type {
  AngleUnit,
  Axis,
  EulerFrame,
  EulerOrder,
  Evidence,
  GripperQuantity,
  Hand,
  HandAction,
  HandRole,
  HandRotation,
  LengthUnit,
  Mapping,
  OrientationKind,
  QuaternionOrder,
  ReferenceTransform,
  Selection,
  Side,
  Tcp,
} from "../contract/mappingSchema.generated";

export type {
  AngleUnit,
  Axis,
  Camera,
  EulerFrame,
  EulerOrder,
  Gripper,
  GripperQuantity,
  Hand,
  HandAction,
  HandRole,
  HandRotation,
  LengthUnit,
  Mapping,
  Orientation,
  OrientationKind,
  Position,
  QuaternionOrder,
  ReferenceTransform,
  ReferenceTransformKind,
  Selection,
  Side,
  Tcp,
  Evidence,
} from "../contract/mappingSchema.generated";

export const MAPPING_SCHEMA_VERSION = 2;

// 선택지 목록은 화면이 순서대로 보인다. 타입은 생성 union 이 정한다. 목록이 스키마 enum 과 같은지는
// 테스트가 고정한다.
export const HAND_SIDES = ["left", "right", "single"] as const satisfies readonly Side[];

/** 한 손의 두 역할. action 은 선택이다. 관측을 action 으로 복사하지 않는다. */
export const HAND_ROLE_NAMES = ["observation", "action"] as const;
export type HandRoleName = (typeof HAND_ROLE_NAMES)[number];

export const HAND_AXES = ["+x", "-x", "+y", "-y", "+z", "-z"] as const satisfies readonly Axis[];

export const EULER_ORDERS = [
  "XYZ",
  "XZY",
  "YXZ",
  "YZX",
  "ZXY",
  "ZYX",
] as const satisfies readonly EulerOrder[];

export const LENGTH_UNITS = ["m", "cm", "mm"] as const satisfies readonly LengthUnit[];

export const ORIENTATION_KINDS = [
  "quaternion",
  "euler",
  "matrix",
] as const satisfies readonly OrientationKind[];

export const QUATERNION_ORDERS = ["wxyz", "xyzw"] as const satisfies readonly QuaternionOrder[];

export const EULER_FRAMES = ["intrinsic", "extrinsic"] as const satisfies readonly EulerFrame[];

export const ANGLE_UNITS = ["deg", "rad"] as const satisfies readonly AngleUnit[];

export const GRIPPER_QUANTITIES = [
  "opening_width",
  "joint_angle",
  "encoder",
  "normalized",
] as const satisfies readonly GripperQuantity[];

/** 위치 3 · quaternion 4 · euler 3 · matrix 9 · 그리퍼 1. 종류를 고르기 전의 자세는 0 이다. */
export const POSITION_COLUMN_COUNT = 3;
export const GRIPPER_COLUMN_COUNT = 1;

export function orientationColumnCount(kind: OrientationKind | null): number {
  switch (kind) {
    case "quaternion":
      return 4;
    case "euler":
      return 3;
    case "matrix":
      return 9;
    case null:
      return 0;
  }
}

/**
 * 보정하지 않는 손 축. `+x` / `+y` 와 0 도는 항등이다. 부를 때마다 새 값을 준다. 편집본들이 한 객체를
 * 나눠 갖지 않게 한다.
 */
export function identityHandRotation(): HandRotation {
  return { x_axis: "+x", y_axis: "+y", degrees: [0, 0, 0] };
}

/** 손 축 보정이 없는지. BE 는 항등 보정을 곱하지 않는다. */
export function isIdentityRotation(rotation: HandRotation): boolean {
  return (
    rotation.x_axis === "+x" &&
    rotation.y_axis === "+y" &&
    rotation.degrees.every((degree) => degree === 0)
  );
}

export function emptyMapping(): Mapping {
  return {
    schema_version: MAPPING_SCHEMA_VERSION,
    robot_model: { name: null, basis: null },
    gripper_model: { name: null, basis: null },
    step_rate: { hz: null, basis: null },
    hands: [],
    cameras: [],
    time_shift_seconds: 0,
  };
}

/** 열을 하나도 고르지 않은 선택. */
export function emptySelection(channel: string | null, count: number): Selection {
  return { channel, columns: Array.from({ length: count }, () => null) };
}

/** 자료와 적용 범위가 확인되지 않은 근거. 빈 칸을 확인 상태로 바꾸지 않는다. */
export function emptyEvidence(): Evidence {
  return {
    basis: null,
    sources: [],
    scope: {
      revision_uuid: null,
      side: null,
      role: null,
      robot_model: null,
      gripper_model: null,
      mounting: null,
      recording_configuration: null,
      reference_frame: null,
      tool_frame: null,
      applicability: null,
    },
    official_search: { status: "not_investigated", searched_locations: [], summary: null },
    inference: null,
  };
}

/** 원점·이동량·기적용 여부를 각각 미확인 상태로 만든다. */
export function emptyTcp(): Tcp {
  return {
    origin: { point: null, definition: null, evidence: emptyEvidence() },
    transform: {
      kind: null,
      axes: null,
      target: "sigmakit_closed_tcp",
      x_mm: null,
      y_mm: null,
      z_mm: null,
      evidence: emptyEvidence(),
    },
    applied_correction: { state: null, description: null, evidence: emptyEvidence() },
  };
}

/**
 * 옮기지 않는 기준 변환(identity). 원본 pose 가 이미 `reference_frame` 좌표계라는 뜻이다. 근거는 fixed 일 때만
 * 평가하지만 칸은 항상 있다.
 */
export function identityReferenceTransform(): ReferenceTransform {
  return {
    kind: "identity",
    source_frame: null,
    quaternion_wxyz: [null, null, null, null],
    x_m: null,
    y_m: null,
    z_m: null,
    evidence: emptyEvidence(),
  };
}

/** 아무 칸도 채우지 않은 역할. 구성요소는 있고 그 안의 칸이 `null` 이다. */
export function emptyRole(): HandRole {
  return {
    position: { source: emptySelection(null, POSITION_COLUMN_COUNT), unit: null, basis: null },
    orientation: {
      source: emptySelection(null, orientationColumnCount(null)),
      kind: null,
      quaternion_order: null,
      euler_order: null,
      euler_frame: null,
      angle_unit: null,
      normalize_quaternion: false,
      basis: null,
    },
    gripper: {
      source: emptySelection(null, GRIPPER_COLUMN_COUNT),
      quantity: null,
      unit: null,
      input_closed: null,
      input_open: null,
      clip_range: [null, null],
      basis: null,
    },
    reference_frame: null,
    target_frame: null,
    reference_transform: identityReferenceTransform(),
    pose_kind: null,
    tcp: emptyTcp(),
    rotation: identityHandRotation(),
    reinterpret_reason: null,
  };
}

/** 아무 칸도 채우지 않은 action. 관측과 같은 칸에 action 값의 종류가 더 있다. */
export function emptyAction(): HandAction {
  return { ...emptyRole(), motion: { kind: null, delta_frame: null, basis: null } };
}

/** 새 손. 관측은 빈 칸으로 시작하고 action 은 내보내지 않는다. */
export function emptyHand(side: Side): Hand {
  return {
    side,
    observation: emptyRole(),
    action: null,
  };
}

/** 채널과 모든 열을 서로 다르게 골랐는지. */
export function isSelectionComplete(selection: Selection): boolean {
  const { channel, columns } = selection;
  if (channel === null || channel.trim() === "" || columns.length === 0) {
    return false;
  }
  const chosen = columns.filter(
    (column): column is number => column !== null && Number.isInteger(column) && column >= 0,
  );
  return chosen.length === columns.length && repeatedColumnIndexes(columns).length === 0;
}

/** 다른 칸과 같은 열을 고른 칸의 번호. 예) `[0, 0, 1]` → `[0, 1]`. 고르지 않은 칸은 보지 않는다. */
export function repeatedColumnIndexes(columns: readonly (number | null)[]): number[] {
  return columns.flatMap((column, index) =>
    column !== null && columns.indexOf(column) !== columns.lastIndexOf(column) ? [index] : [],
  );
}

const SELECTION_FIELDS = ["position", "orientation", "gripper"] as const;

/**
 * 같은 열을 두 번 고른 칸의 leaf 경로. 예) `hands.0.observation.position.source.columns.1`.
 *
 * BE 는 중복 열이 있는 매핑을 저장 · 검증 · POC 모두에서 422 로 거부한다. 정본 스키마는 이 규칙을 적을 수
 * 없다. 그래서 FE 가 보내기 전에 이 목록으로 막는다.
 */
export function repeatedColumnPaths(mapping: Mapping): string[] {
  return mapping.hands.flatMap((hand, handIndex) =>
    HAND_ROLE_NAMES.flatMap((roleName) => {
      const role = hand[roleName];
      if (role === null) {
        return [];
      }
      return SELECTION_FIELDS.flatMap((field) =>
        repeatedColumnIndexes(role[field].source.columns).map(
          (column) => `hands.${handIndex}.${roleName}.${field}.source.columns.${column}`,
        ),
      );
    }),
  );
}

/**
 * BE 가 매핑 · 원본에서 확인한 문제 하나. `path` 는 매핑 경로다(예: `hands.0.observation.position`).
 * 매핑 밖의 문제는 `source`(원본) · `time` · `sample` · `evaluation` 이다. 큰 ns 시각은 문자열로 온다.
 */
export type MappingIssue = {
  readonly code: string;
  readonly path: string;
  /** BE 원문(영어). 화면은 코드로 문장을 고르고 이 값은 기술 정보로만 보인다. */
  readonly message: string;
  readonly severity: "error" | "warning";
  readonly channel: string | null;
  readonly otherChannel: string | null;
  readonly row: number | null;
  readonly timestamp: string | null;
  readonly otherTimestamp: string | null;
  readonly timestampUnit: string | null;
  readonly otherTimestampUnit: string | null;
};

/** episode 하나의 수치 검증. `valid` 는 영상 디코드 검사 성공을 뜻하지 않는다. */
export type EpisodeValidation = {
  readonly sourceId: string;
  readonly valid: boolean;
  readonly issues: readonly MappingIssue[];
};

/** 검증 요청 하나의 결과. 검사한 매핑 · 샘플 · 구현 버전에 묶여 있다. */
export type MappingValidation = {
  readonly validationUuid: string;
  readonly revisionUuid: string;
  readonly tcpAssessments: readonly {
    readonly path: string;
    readonly status: "blocked" | "requires_validation" | "requires_experimental_review";
    readonly issues: readonly MappingIssue[];
  }[];
  readonly mappingDigest: string;
  readonly probeUuid: string;
  readonly sampleUuid: string;
  readonly sampleManifestDigest: string;
  readonly engineVersion: string;
  readonly templatesVersion: string;
  readonly implementation: string;
  readonly episodes: readonly EpisodeValidation[];
};

export type MappingReview = {
  readonly uuid: string;
  readonly validationUuid: string;
  readonly reviewerUuid: string;
  readonly createdAtMs: number;
  readonly note: string;
};

/** POC 작업 상태. 성공 · 실패는 끝이다. `queued` 는 재시도 대기일 수도 있다(`attempt` 가 늘어난다). */
export const POC_JOB_STATES = ["queued", "running", "succeeded", "failed"] as const;
export type PocJobState = (typeof POC_JOB_STATES)[number];

export function isPocFinished(state: PocJobState): boolean {
  return state === "succeeded" || state === "failed";
}

/**
 * POC 작업 하나. 접수할 때 매핑 · Probe · 샘플 · episode · 구현 버전을 고정한다. 결과는 그 입력에만 속한다.
 * `result` 는 워커 보고서(JSON) 원문이다. `error` 는 마지막 실패의 BE 원문이다.
 */
export type PocJob = {
  readonly validationUuid: string;
  readonly reviewUuid: string | null;
  readonly experimental: boolean;
  readonly uuid: string;
  readonly state: PocJobState;
  readonly attempt: number;
  readonly createdAtMs: number;
  readonly updatedAtMs: number;
  readonly probeUuid: string;
  readonly sampleUuid: string;
  readonly mappingDigest: string;
  readonly inputDigest: string;
  readonly sourceIds: readonly string[];
  readonly engineVersion: string;
  readonly templatesVersion: string;
  readonly implementation: string;
  readonly outputArtifactUuid: string | null;
  readonly result: Readonly<Record<string, unknown>> | null;
  readonly error: string | null;
};
